import { collection, doc, getDoc, getDocs, limit, query, setDoc, deleteDoc, serverTimestamp, Timestamp, where } from 'firebase/firestore';
import { db } from '../lib/firebase';

const FRIEND_LOOKUP_COLLECTION = 'friendLookup';

/** Minimum days between friend username changes (similar to Facebook’s 60-day name change limit). */
export const FRIEND_USERNAME_CHANGE_COOLDOWN_DAYS = 90;

export interface FriendLookupDoc {
  uid: string;
  email: string;
  username: string | null;
  /** When the username was last set or changed (Firestore Timestamp or ms). Used for 90-day change limit. */
  usernameUpdatedAt?: number | Timestamp;
}

/** Normalize email: lowercase, trim */
export function normalizeEmail(email: string): string {
  return email.toLowerCase().trim();
}

/** Normalize username: lowercase, trim. Usernames cannot contain @ */
export function normalizeUsername(username: string): string {
  const trimmed = username.trim().toLowerCase();
  if (trimmed.includes('@')) {
    throw new Error('Username cannot contain @');
  }
  return trimmed;
}

/** Format rules: 6–20 chars, alphanumeric and underscore only */
const USERNAME_REGEX = /^[a-z0-9_]{6,20}$/;

export function validateUsernameFormat(username: string): { valid: boolean; error?: string } {
  const normalized = username.trim().toLowerCase();
  if (normalized.length < 6) {
    return { valid: false, error: 'Username must be at least 6 characters' };
  }
  if (normalized.length > 20) {
    return { valid: false, error: 'Username must be 20 characters or less' };
  }
  if (!USERNAME_REGEX.test(normalized)) {
    return {
      valid: false,
      error: 'Username can only contain letters, numbers, and underscores',
    };
  }
  return { valid: true };
}

/**
 * Exact-match blocklist: reserved or disallowed full usernames.
 */
const RESERVED_USERNAMES: Set<string> = new Set([
  'admin',
  'administrator',
  'support',
  'help',
  'cruise',
  'cruisetracker',
  'null',
  'undefined',
  'me',
  'you',
  'someone',
  'unknown',
  'anonymous',
  'test',
  'demo',
  'root',
  'system',
  'mod',
  'moderator',
  'official',
  'staff',
]);

/**
 * Substrings that may not appear anywhere in a username (profanity, slurs, etc.).
 * Usernames containing any of these are rejected (e.g. "fuckoff" contains "fuck").
 * Keep lowercase; username is normalized before check.
 */
const BLOCKED_SUBSTRINGS: readonly string[] = [
  'fuck',
  'shit',
  'bitch',
  'bastard',
  'cunt',
  'dick',
  'cock',
  'pussy',
  'whore',
  'slut',
  'nigger',
  'nigga',
  'fag',
  'faggot',
  'retard',
  'rape',
  'rapist',
  'nazi',
  'hitler',
  'kys',
];

/**
 * Terms that are blocked only when they appear as a standalone "word" (word-boundary),
 * so we don't block usernames like "pass" or "classic" where the term is inside a word.
 * A word boundary here: start of string, end of string, underscore, or digit (not a letter).
 * E.g. "ass" blocks: "ass", "ass_", "_ass", "ass_foo", "foo_ass", "ass1". Does not block: "pass", "classic", "glass".
 */
const BLOCKED_WORD_BOUNDARY_TERMS: readonly string[] = ['ass'];

function escapeRegex(s: string): string {
  return s.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

/** Returns true if the normalized username contains the term as a standalone word (delimited by ^ | _ | digit | $). */
function containsWordBoundaryTerm(normalized: string, term: string): boolean {
  const escaped = escapeRegex(term);
  const re = new RegExp(`(^|_|[0-9])${escaped}($|_|[0-9])`);
  return re.test(normalized);
}

export function isUsernameDerogatory(username: string): boolean {
  const normalized = normalizeUsername(username);
  if (RESERVED_USERNAMES.has(normalized)) {
    return true;
  }
  for (const term of BLOCKED_SUBSTRINGS) {
    if (normalized.includes(term)) {
      return true;
    }
  }
  for (const term of BLOCKED_WORD_BOUNDARY_TERMS) {
    if (containsWordBoundaryTerm(normalized, term)) {
      return true;
    }
  }
  return false;
}

/**
 * Get friendLookup document by normalized email.
 */
export async function getLookupByEmail(email: string): Promise<FriendLookupDoc | null> {
  const key = normalizeEmail(email);
  const ref = doc(db, FRIEND_LOOKUP_COLLECTION, key);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    uid: data.uid,
    email: data.email ?? key,
    username: data.username ?? null,
  };
}

/**
 * Get friendLookup document by uid (stable across email/username changes).
 * Use this when you have a friendId and need current email and username for display.
 */
export async function getLookupByUid(uid: string): Promise<FriendLookupDoc | null> {
  const ref = collection(db, FRIEND_LOOKUP_COLLECTION);
  const q = query(ref, where('uid', '==', uid), limit(1));
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const data = snap.docs[0].data();
  return {
    uid: data.uid,
    email: data.email ?? '',
    username: data.username ?? null,
  };
}

/**
 * Get friendLookup document by normalized username.
 */
export async function getLookupByUsername(username: string): Promise<FriendLookupDoc | null> {
  const key = normalizeUsername(username);
  const ref = doc(db, FRIEND_LOOKUP_COLLECTION, key);
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  const data = snap.data();
  return {
    uid: data.uid,
    email: data.email ?? '',
    username: data.username ?? key,
  };
}

/**
 * Resolve an input (email or username) to a FriendLookupDoc.
 * If input contains @ (and is an email address, not a @username handle), treat as email; otherwise treat as username.
 */
export async function resolveEmailOrUsername(
  input: string
): Promise<{ uid: string; email: string; username: string | null } | null> {
  const trimmed = input.trim();
  if (!trimmed) return null;

  // If user typed leading '@' (e.g. "@captain_john"), treat as username
  if (trimmed.startsWith('@')) {
    const cleanUsername = trimmed.slice(1).trim();
    if (!cleanUsername) return null;
    const doc = await getLookupByUsername(cleanUsername);
    return doc ? { uid: doc.uid, email: doc.email, username: doc.username } : null;
  }

  if (trimmed.includes('@')) {
    const doc = await getLookupByEmail(trimmed);
    return doc ? { uid: doc.uid, email: doc.email, username: doc.username } : null;
  }
  const doc = await getLookupByUsername(trimmed);
  return doc ? { uid: doc.uid, email: doc.email, username: doc.username } : null;
}

/**
 * Check if the user (by email or uid) has set a friend username.
 */
export async function hasFriendUsername(email: string): Promise<boolean> {
  const lookup = await getLookupByEmail(email);
  return lookup != null && lookup.username != null && lookup.username.length > 0;
}

/**
 * Set the current user's friend username. Creates friendLookup docs (username keyed, and email keyed if non-relay).
 * Validates: format, unique, not in use, not derogatory.
 */
export async function setFriendUsername(
  uid: string,
  email: string,
  username: string
): Promise<void> {
  const normalizedEmail = normalizeEmail(email);
  const normalizedUsername = normalizeUsername(username);

  const formatCheck = validateUsernameFormat(username);
  if (!formatCheck.valid) {
    throw new Error(formatCheck.error);
  }
  if (isUsernameDerogatory(username)) {
    throw new Error('This username is not allowed. Please choose another.');
  }

  const existingByUsername = await getLookupByUsername(normalizedUsername);
  if (existingByUsername && existingByUsername.uid !== uid) {
    throw new Error('This username is already in use. Please choose another.');
  }

  const docData = {
    uid,
    email: normalizedEmail,
    username: normalizedUsername,
    usernameUpdatedAt: serverTimestamp(),
  };

  const isRelayEmail = normalizedEmail.endsWith('privaterelay.appleid.com');
  if (!isRelayEmail) {
    await setDoc(doc(db, FRIEND_LOOKUP_COLLECTION, normalizedEmail), docData);
  }
  await setDoc(doc(db, FRIEND_LOOKUP_COLLECTION, normalizedUsername), docData);
}

function getUpdatedAtMillis(data: { usernameUpdatedAt?: unknown }): number | null {
  const v = data.usernameUpdatedAt;
  if (v == null) return null;
  if (typeof v === 'number') return v;
  if (v && typeof (v as Timestamp).toMillis === 'function') return (v as Timestamp).toMillis();
  return null;
}

/**
 * Update the current user's friend username. Use when the user already has a username and is changing it.
 * Deletes the old username doc so the previous name no longer resolves to this user, then sets the new one.
 * Enforces a 90-day cooldown between changes (similar to Facebook’s name change limit).
 */
export async function updateFriendUsername(
  uid: string,
  email: string,
  newUsername: string,
  oldUsername: string | null
): Promise<void> {
  const normalizedEmail = normalizeEmail(email);
  const normalizedNew = normalizeUsername(newUsername);

  if (oldUsername != null && oldUsername.trim() !== '') {
    const emailRef = doc(db, FRIEND_LOOKUP_COLLECTION, normalizedEmail);
    const emailSnap = await getDoc(emailRef);
    const updatedAtMs = emailSnap.exists() ? getUpdatedAtMillis(emailSnap.data()) : null;
    if (updatedAtMs != null) {
      const msPerDay = 24 * 60 * 60 * 1000;
      const daysSince = (Date.now() - updatedAtMs) / msPerDay;
      if (daysSince < FRIEND_USERNAME_CHANGE_COOLDOWN_DAYS) {
        const nextDate = new Date(updatedAtMs);
        nextDate.setDate(nextDate.getDate() + FRIEND_USERNAME_CHANGE_COOLDOWN_DAYS);
        throw new Error(
          `You can only change your friend username once every ${FRIEND_USERNAME_CHANGE_COOLDOWN_DAYS} days. ` +
          `You can change it again after ${nextDate.toLocaleDateString()}.`
        );
      }
    }

    const normalizedOld = normalizeUsername(oldUsername);
    if (normalizedOld !== normalizedNew) {
      const oldRef = doc(db, FRIEND_LOOKUP_COLLECTION, normalizedOld);
      await deleteDoc(oldRef);
    }
  }

  await setFriendUsername(uid, email, newUsername);
}
